import { PersonAdd } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
} from '@mui/material';
import { Signer } from 'lib/api/business/types';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import React, { useMemo } from 'react';

import { Aval, AvalType, AvalTypeOptions } from '../types';
import AvalCard from './AvalCard';
import OtherCompany from './OtherCompany';
import SameCompany from './SameCompany';
import Spouse from './Spouse';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  aval: Aval;
  setAval: (aval: Aval) => void;
  value: Aval[];
  onChange: (value: Aval[]) => void;
  businessUsers: Signer[];
};

const AvalsDialog: React.FC<Props> = ({
  open,
  setOpen,
  aval,
  setAval,
  value,
  onChange,
  businessUsers,
}) => {
  const setAvalType = (type: AvalType) => {
    if (!aval) return;
    const newAval = { ...aval, type };
    setAval(newAval);
  };

  const handleAddAval = () => {
    onChange([...(value || []), aval]);
    setAval({ type: 'SAME_COMPANY' });
  };

  const handleDeleteAval = (index: number) => {
    const newAvals = (value || []).filter((_, i) => i !== index);
    onChange(newAvals);
  };

  const disabledAval = useMemo(() => {
    if (aval?.type === 'SPOUSE') {
      return (
        !aval.signer?.name ||
        !aval.signer?.email ||
        !aval.signer?.phone ||
        !aval.signer?.identifier ||
        !aval.signer?.profession ||
        !aval.spouseRelation?.identifier ||
        !aval.spouseRelation?.name
      );
    } else if (aval?.type === 'SAME_COMPANY') {
      return !aval.signer;
    } else if (aval?.type === 'OTHER_COMPANY') {
      return !aval.signer || !aval.business?.identifier || !aval.business.name;
    }
    return false;
  }, [aval]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle
        onClose={() => setOpen(false)}
        title="Agregar aval"
        icon={<PersonAdd color="primary" />}
        color="primary"
      />
      <DialogContent>
        <Stack direction="column" spacing={4}>
          <Stack direction="row" justifyContent="space-between">
            <CustomToggleButtonGroup
              options={AvalTypeOptions}
              value={aval?.type ?? ''}
              onChange={setAvalType}
            />
            <CustomButton
              startIcon={<AddIcon />}
              onClick={handleAddAval}
              disabled={disabledAval}
              size="small"
            >
              Agregar aval
            </CustomButton>
          </Stack>
          {aval?.type === 'SAME_COMPANY' && (
            <SameCompany
              aval={aval}
              setAval={setAval}
              value={value}
              businessUsers={businessUsers}
            />
          )}
          {aval?.type === 'OTHER_COMPANY' && (
            <OtherCompany aval={aval} setAval={setAval} value={value} />
          )}
          {aval?.type === 'SPOUSE' && (
            <Spouse aval={aval} setAval={setAval} value={value} />
          )}
        </Stack>
        {value?.length > 0 && <Divider sx={{ my: 3 }} />}
        <Stack direction="column" spacing={2}>
          {value?.map((aval, index) => (
            <AvalCard
              key={`${aval.signer?.identifier}-${index}`}
              aval={aval}
              index={index}
              onDelete={handleDeleteAval}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={() => setOpen(false)}>Listo</CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AvalsDialog;
